/**
 * 설정 관리 커스텀 훅
 * 애플리케이션 전체에서 설정을 관리하고 적용
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  loadSettings, 
  saveSettings, 
  updateSetting,
  addSettingsListener,
  removeSettingsListener,
  DEFAULT_SETTINGS
} from '../utils/settingsManager';

/**
 * 설정 관리 훅
 * @returns {Object} 설정 상태와 관리 함수들
 */
export function useSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // 설정 불러오기
  const loadCurrentSettings = useCallback(() => {
    setIsLoading(true);
    try {
      const currentSettings = loadSettings();
      setSettings(currentSettings);
      applySettingsToDOM(currentSettings);
    } catch (error) {
      console.error('설정 불러오기 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 컴포넌트 마운트시 설정 불러오기
  useEffect(() => {
    loadCurrentSettings();
  }, [loadCurrentSettings]);

  // 설정 변경 리스너 등록
  useEffect(() => {
    const handleSettingsChange = (event) => {
      const newSettings = event.detail;
      setSettings(newSettings);
      applySettingsToDOM(newSettings);
    };

    addSettingsListener(handleSettingsChange);

    return () => {
      removeSettingsListener(handleSettingsChange);
    };
  }, []);

  // DOM에 설정 적용
  const applySettingsToDOM = useCallback((settingsToApply) => {
    const root = document.documentElement;

    // 테마 적용
    if (settingsToApply.theme === 'auto') {
      // 시스템 테마 감지
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.setAttribute('data-theme', systemTheme);
      root.classList.toggle('theme-dark', systemTheme === 'dark');
      root.classList.toggle('theme-light', systemTheme === 'light');
    } else {
      root.setAttribute('data-theme', settingsToApply.theme);
      root.classList.toggle('theme-dark', settingsToApply.theme === 'dark');
      root.classList.toggle('theme-light', settingsToApply.theme === 'light');
    }

    // 접근성 설정 적용
    root.classList.toggle('high-contrast', settingsToApply.highContrast);
    root.classList.toggle('large-fonts', settingsToApply.largeFonts);
    root.classList.toggle('no-animations', !settingsToApply.enableAnimations);

    // 애니메이션 CSS 변수 설정
    if (settingsToApply.enableAnimations) {
      root.style.setProperty('--animation-duration', '0.3s');
      root.style.setProperty('--transition-duration', '0.2s');
    } else {
      root.style.setProperty('--animation-duration', '0ms');
      root.style.setProperty('--transition-duration', '0ms');
    }
  }, []);

  // 개별 설정 업데이트
  const updateSettingValue = useCallback(async (key, value) => {
    try {
      const success = updateSetting(key, value);
      if (success) {
        const updatedSettings = { ...settings, [key]: value };
        setSettings(updatedSettings);
        applySettingsToDOM(updatedSettings);
        return true;
      }
      return false;
    } catch (error) {
      console.error('설정 업데이트 실패:', error);
      return false;
    }
  }, [settings, applySettingsToDOM]);

  // 모든 설정 저장
  const saveAllSettings = useCallback(async (newSettings) => {
    try {
      const success = saveSettings(newSettings);
      if (success) {
        setSettings(newSettings);
        applySettingsToDOM(newSettings);
        return true;
      }
      return false;
    } catch (error) {
      console.error('설정 저장 실패:', error);
      return false;
    }
  }, [applySettingsToDOM]);

  // 테마 토글 (라이트 <-> 다크)
  const toggleTheme = useCallback(() => {
    const currentTheme = settings.theme;
    let newTheme;
    
    if (currentTheme === 'auto') {
      // auto에서 다크로
      newTheme = 'dark';
    } else if (currentTheme === 'dark') {
      // 다크에서 라이트로
      newTheme = 'light';
    } else {
      // 라이트에서 auto로
      newTheme = 'auto';
    }
    
    return updateSettingValue('theme', newTheme);
  }, [settings.theme, updateSettingValue]);

  // 자동 로그아웃 시간 확인
  const getAutoLogoutMs = useCallback(() => {
    const minutes = parseInt(settings.autoLogoutTime);
    return minutes === 0 ? 0 : minutes * 60 * 1000;
  }, [settings.autoLogoutTime]);

  // 복사 알림 표시 여부
  const shouldShowCopyNotification = useCallback(() => {
    return settings.showCopyNotification;
  }, [settings.showCopyNotification]);

  return {
    // 설정 상태
    settings,
    isLoading,
    
    // 설정 관리 함수
    updateSetting: updateSettingValue,
    saveSettings: saveAllSettings,
    loadSettings: loadCurrentSettings,
    
    // 편의 함수
    toggleTheme,
    getAutoLogoutMs,
    shouldShowCopyNotification,
    
    // 설정값 직접 접근
    theme: settings.theme,
    autoLogoutTime: settings.autoLogoutTime,
    showCopyNotification: settings.showCopyNotification,
    enableAnimations: settings.enableAnimations,
    highContrast: settings.highContrast,
    largeFonts: settings.largeFonts
  };
}

/**
 * 시스템 테마 변경 감지 훅
 */
export function useSystemTheme() {
  const [systemTheme, setSystemTheme] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return systemTheme;
}
